/**
 * Sync Service with Effect Stream + Schedule
 * Handles full/delta sync, pagination, and retry semantics declaratively
 */

import * as Effect from 'effect/Effect';
import * as Stream from 'effect/Stream';
import * as Schedule from 'effect/Schedule';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';
import { pipe } from 'effect/Function';
import { ApiClient } from '@/api/client';
import { StorageService, type TransactionRecord } from '../storage/storage.service';
import { Logger } from '../logger/logger.service';
import { Clock } from '../clock/clock.service';
import { Metrics } from '../metrics/metrics.service';
import { RepositoryEvents, Events } from '../events/repository-events';
import { ConflictResolver } from '../conflict/conflict-resolver.service';
import * as SyncState from './sync-state';
import type { SyncMetadata } from './sync-state';

// Sync errors
export class SyncError extends Error {
  readonly _tag = 'SyncError';
  constructor(
    message: string,
    readonly cause?: unknown,
    readonly groupId?: string
  ) {
    super(message);
  }
}

export class NetworkError extends Error {
  readonly _tag = 'NetworkError';
  constructor(
    message: string,
    readonly cause?: unknown
  ) {
    super(message);
  }
}

// Sync options
export interface SyncOptions {
  readonly groupId: string;
  readonly mode: 'full' | 'incremental';
  readonly maxRetries?: number;
  readonly pageSize?: number;
}

// Page of data from server
export interface ServerPage {
  readonly transactions: readonly any[];
  readonly cursor: string | null;
  readonly hasMore: boolean;
  readonly notModified?: boolean; // True when server returns 304 Not Modified
}

// Sync service interface
export interface SyncServiceInterface {
  // Sync a group (full or incremental)
  readonly syncGroup: (options: SyncOptions) => Effect.Effect<SyncResult, SyncError>;

  // Sync as a Stream for progress tracking
  readonly syncGroupStream: (options: SyncOptions) => Stream.Stream<SyncProgress, SyncError>;

  // Push local changes to server
  readonly pushLocalChanges: (groupId: string) => Effect.Effect<PushResult, SyncError>;

  // Get sync status for a group
  readonly getSyncStatus: (groupId: string) => Effect.Effect<SyncMetadata | null, never>;
}

export class SyncService extends Context.Tag('SyncService')<
  SyncService,
  SyncServiceInterface
>() {}

// Result types
export interface SyncResult {
  readonly groupId: string;
  readonly recordsSynced: number;
  readonly conflicts: number;
  readonly duration: number;
}

export interface SyncProgress {
  readonly _tag: 'Progress';
  readonly pagesFetched: number;
  readonly recordsFetched: number;
  readonly hasMore: boolean;
}

export interface PushResult {
  readonly pushed: number;
  readonly failed: number;
}

// Helper to fetch a single page - now takes dependencies as parameters
const makeFetchPage = (api: Context.Tag.Service<ApiClient>, logger: Context.Tag.Service<Logger>) =>
  (groupId: string, cursor: string | null, pageSize: number) =>
    Effect.gen(function* () {
      const syncCacheBust = Date.now();
      const url = cursor
        ? `/groups/${groupId}/expenses?cursor=${cursor}&limit=${pageSize}&syncCacheBust=${syncCacheBust}`
        : `/groups/${groupId}/expenses?limit=${pageSize}&syncCacheBust=${syncCacheBust}`;

      yield* logger.debug('sync', 'Fetching page', { groupId, cursor, pageSize });

      const response: any = yield* api.get(url);

      // Check for 304 Not Modified response
      if (response?.notModified === true) {
        return {
          transactions: [],
          cursor: null,
          hasMore: false,
          notModified: true,
        } as ServerPage;
      }

      // Parse response
      let transactions: any[] = [];
      let nextCursor: string | null = null;
      let hasMore = false;

      const responseCursor = response?.cursor
        || response?.next_cursor
        || response?.nextCursor
        || response?.pagination?.next_cursor
        || response?.pagination?.nextCursor
        || null;
      const responseHasMore = response?.hasMore ?? response?.pagination?.hasMore;

      if (Array.isArray(response)) {
        transactions = response;
      } else if (response?.expenses && Array.isArray(response.expenses)) {
        transactions = response.expenses;
        nextCursor = responseCursor;
        // Use server's hasMore if provided, otherwise infer from cursor and page size
        hasMore = responseHasMore !== undefined ? responseHasMore : (!!nextCursor && transactions.length >= pageSize);
      } else if (response?.data && Array.isArray(response.data)) {
        transactions = response.data;
        nextCursor = responseCursor;
        // Use server's hasMore if provided, otherwise infer from cursor and page size
        hasMore = responseHasMore !== undefined ? responseHasMore : (!!nextCursor && transactions.length >= pageSize);
      }

      // For array responses, infer hasMore from cursor presence and page size
      if (Array.isArray(response)) {
        hasMore = !!nextCursor && transactions.length >= pageSize;
      }

      return {
        transactions,
        cursor: nextCursor,
        hasMore,
      } as ServerPage;
    });

// Retry schedule: exponential backoff with max 3 retries
const retrySchedule = Schedule.exponential('100 millis').pipe(
  Schedule.compose(Schedule.recurs(3))
);

// Live implementation
export const SyncServiceLive = Layer.effect(
  SyncService,
  Effect.gen(function* () {
    const storage = yield* StorageService;
    const logger = yield* Logger;
    const clock = yield* Clock;
    yield* Metrics; // Available for future use
    const events = yield* RepositoryEvents;
    yield* ConflictResolver; // Available for future use
    const api = yield* ApiClient; // Get API client at top level

    // Create fetchPage with bound dependencies
    const fetchPage = makeFetchPage(api, logger);

    return {
      syncGroupStream: (options) => {
        // Real implementation that emits progress per page
        return Stream.asyncEffect<SyncProgress, SyncError, never>((emit) =>
          pipe(
            Effect.gen(function* () {
              let cursor: string | null = null;
              let pageCount = 0;
              let totalRecords = 0;

              // Pagination loop with progress emissions
              while (pageCount < 100) {
                const page: ServerPage = yield* pipe(
                  fetchPage(options.groupId, cursor, options.pageSize || 100),
                  Effect.retry(retrySchedule),
                  Effect.mapError(error => new SyncError('Failed to fetch page', error, options.groupId))
                );

                totalRecords += page.transactions.length;
                pageCount++;

                // Emit progress after each page
                yield* Effect.promise(() => emit.single({
                  _tag: 'Progress' as const,
                  pagesFetched: pageCount,
                  recordsFetched: totalRecords,
                  hasMore: page.hasMore,
                }));

                if (!page.hasMore) {
                  break;
                }

                cursor = page.cursor;
              }

              // Signal stream completion
              yield* Effect.promise(() => emit.end());
            }),
            Effect.mapError(error =>
              error instanceof SyncError
                ? error
                : new SyncError('Stream failed', error, options.groupId)
            )
          )
        );
      },

      syncGroup: (options) => {
        const effect = Effect.gen(function* () {
          const startTime = yield* clock.timestamp();
          const timestamp = yield* clock.isoString();

          yield* logger.info('sync', 'Starting sync', {
            groupId: options.groupId,
            mode: options.mode,
          });

          // Update sync metadata to Fetching state
          const metadata = yield* storage.getSyncMetadata(options.groupId);
          const currentGeneration = metadata?.generation || 0;

          yield* storage.putSyncMetadata({
            groupId: options.groupId,
            state: SyncState.Fetching(timestamp, null, 0, 0),
            lastFullSyncAt: metadata?.lastFullSyncAt || null,
            lastIncrementalSyncAt: metadata?.lastIncrementalSyncAt || null,
            cursor: null,
            generation: currentGeneration + 1,
          });

          // Emit state change event
          yield* events.publish(
            Events.syncStateChanged(
              options.groupId,
              metadata?.state || SyncState.Idle(),
              SyncState.Fetching(timestamp, null, 0, 0),
              timestamp
            )
          );

          // Fetch both expenses and settlements
          let totalRecords = 0;
          let cursor: string | null = null;
          let pageCount = 0;

          // Track all server transaction IDs for cleanup
          const serverTransactionIds = new Set<string>();

          // Track if any 304 Not Modified responses were received
          let receivedNotModified = false;

          // First, fetch expenses with pagination
          yield* logger.info('sync', 'Starting expense fetch', { groupId: options.groupId });
          while (pageCount < 100) {
            const page: ServerPage = yield* pipe(
              fetchPage(options.groupId, cursor, options.pageSize || 100),
              Effect.retry(retrySchedule),
              Effect.mapError(error => new SyncError('Failed to fetch expenses page', error, options.groupId))
            );

            // Check if this was a 304 Not Modified response
            if (page.notModified) {
              receivedNotModified = true;
              break; // No more data to fetch
            }

            // Check for existing local transactions to preserve metadata
            const existingTransactions = yield* storage.getTransactionsByGroup(options.groupId);
            const existingMap = new Map(existingTransactions.map(t => [t.id, t]));

            // Records with un-pushed local mutations (edits, deletes, or
            // pending creates) must win over incoming server state — otherwise
            // a fetch reverts a local edit or resurrects a pending delete.
            // getUnsyncedTransactions includes soft-deleted rows (it filters on
            // is_synced, not deleted), which getTransactionsByGroup omits.
            const unsyncedLocal = yield* storage.getUnsyncedTransactions();
            const protectedLocal = new Map(
              unsyncedLocal
                .filter(t => t.group_id === options.groupId)
                .map(t => [t.id, t])
            );

            // Store transactions with enriched data from server
            const transactionsToStore: TransactionRecord[] = page.transactions.map((tx: any) => {
              // Track this server transaction ID
              serverTransactionIds.add(tx.id);

              const existing = existingMap.get(tx.id);

              // Determine type from server data
              // Settlements have from_user_id and to_user_id, expenses have category_id and paid_by
              const isSettlement = !!(tx.from_user_id && tx.to_user_id);
              const type = isSettlement ? 'settlement' as const : 'expense' as const;

              // Base record from server
              const baseRecord: TransactionRecord = {
                id: tx.id,
                type,
                group_id: tx.group_id,
                created_at: tx.created_at,
                updated_at: tx.updated_at,
                is_synced: true,
                local_changes: false,
                description: tx.description,
                amount: tx.amount,
                currency_code: tx.currency_code,

                // Expense-specific fields
                category_id: !isSettlement ? tx.category_id : undefined,
                paid_by: !isSettlement ? tx.paid_by : undefined,
                created_by: !isSettlement ? tx.created_by : undefined,
                recurring_expense_id: !isSettlement ? tx.recurring_expense_id ?? null : undefined,
                occurrence_date: !isSettlement ? tx.occurrence_date ?? null : undefined,

                // Settlement-specific fields
                from_user_id: isSettlement ? tx.from_user_id : undefined,
                to_user_id: isSettlement ? tx.to_user_id : undefined,

                // Enriched fields from server (expense)
                user: !isSettlement && tx.user ? {
                  id: tx.user.id,
                  name: tx.user.name,
                  email: tx.user.email,
                  avatar_url: tx.user.avatar_url,
                } : undefined,
                category: !isSettlement && tx.category ? {
                  id: tx.category.id,
                  name: tx.category.name,
                  emoji: tx.category.emoji,
                  pinned: tx.category.pinned,
                } : undefined,
                weights: !isSettlement && tx.weights ? tx.weights.map((w: any) => ({
                  user_id: w.user_id,
                  name: w.name,
                  weight: w.weight,
                })) : undefined,

                // Enriched fields from server (settlement)
                from_user: isSettlement && tx.from_user ? {
                  id: tx.from_user.id,
                  name: tx.from_user.name,
                  email: tx.from_user.email,
                  avatar_url: tx.from_user.avatar_url,
                } : undefined,
                to_user: isSettlement && tx.to_user ? {
                  id: tx.to_user.id,
                  name: tx.to_user.name,
                  email: tx.to_user.email,
                  avatar_url: tx.to_user.avatar_url,
                } : undefined,
              };

              // Never overwrite an un-pushed local mutation with server state.
              // The local copy stays authoritative until it has been pushed;
              // this preserves offline edits and keeps pending deletes deleted.
              const locallyDirty = protectedLocal.get(tx.id);
              if (locallyDirty) {
                return locallyDirty;
              }

              // Preserve local-only metadata if this transaction existed locally
              if (existing) {
                return {
                  ...baseRecord,
                  // Preserve these local fields even after sync
                  sync_generation: existing.sync_generation,
                  sync_error: existing.local_changes ? existing.sync_error : undefined,
                };
              }

              return baseRecord;
            });

            yield* storage.putTransactions(transactionsToStore).pipe(
              Effect.mapError(error => new SyncError('Failed to store transactions', error, options.groupId))
            );

            totalRecords += page.transactions.length;
            pageCount++;

            // Publish progress
            const progressTimestamp = yield* clock.isoString();
            yield* events.publish(
              Events.syncProgress(
                options.groupId,
                pageCount,
                totalRecords,
                page.hasMore,
                progressTimestamp
              )
            );

            if (!page.hasMore) {
              break;
            }

            cursor = page.cursor;
          }

          // Now fetch settlements (no pagination typically)
          yield* logger.info('sync', 'Fetching settlements', { groupId: options.groupId });

          // Try to fetch settlements, but don't fail the entire sync if it fails
          const settlementsResult = yield* Effect.either(
            pipe(
              api.get(`/groups/${options.groupId}/settlements`),
              Effect.retry(retrySchedule)
            )
          );

          let settlements: any[] = [];
          if (settlementsResult._tag === 'Right') {
            const settlementsResponse: any = settlementsResult.right;

            // Check for 304 Not Modified
            if (settlementsResponse?.notModified === true) {
              receivedNotModified = true;
              yield* logger.info('sync', 'Settlements not modified (304)');
            } else {
              // Parse settlements response
              settlements = Array.isArray(settlementsResponse)
                ? settlementsResponse
                : settlementsResponse?.settlements || [];
              yield* logger.info('sync', 'Settlements fetched', { count: settlements.length });
            }
          } else {
            yield* logger.warn('sync', 'Failed to fetch settlements, continuing with expenses only', {}, settlementsResult.left instanceof Error ? settlementsResult.left : undefined);
          }

          if (settlements.length > 0) {
            yield* logger.info('sync', 'Processing settlements', { count: settlements.length });

            const existingTransactions = yield* storage.getTransactionsByGroup(options.groupId);
            const existingMap = new Map(existingTransactions.map(t => [t.id, t]));

            const settlementsToStore: TransactionRecord[] = settlements.map((tx: any) => {
              // Track this server settlement ID
              serverTransactionIds.add(tx.id);

              const existing = existingMap.get(tx.id);

              const baseRecord: TransactionRecord = {
                id: tx.id,
                type: 'settlement',
                group_id: tx.group_id,
                created_at: tx.created_at,
                updated_at: tx.updated_at,
                is_synced: true,
                local_changes: false,
                description: tx.description || '',
                amount: tx.amount,
                currency_code: tx.currency_code,

                // Settlement-specific fields
                from_user_id: tx.from_user_id,
                to_user_id: tx.to_user_id,
                kind: tx.kind,
                origin_event_id: tx.origin_event_id ?? null,

                // Enriched fields from server
                from_user: tx.from_user ? {
                  id: tx.from_user.id,
                  name: tx.from_user.name,
                  email: tx.from_user.email,
                  avatar_url: tx.from_user.avatar_url,
                } : undefined,
                to_user: tx.to_user ? {
                  id: tx.to_user.id,
                  name: tx.to_user.name,
                  email: tx.to_user.email,
                  avatar_url: tx.to_user.avatar_url,
                } : undefined,
              };

              // Preserve local metadata if exists
              if (existing) {
                return {
                  ...baseRecord,
                  sync_generation: existing.sync_generation,
                  sync_error: existing.local_changes ? existing.sync_error : undefined,
                };
              }

              return baseRecord;
            });

            yield* storage.putTransactions(settlementsToStore).pipe(
              Effect.mapError(error => new SyncError('Failed to store settlements', error, options.groupId))
            );

            totalRecords += settlements.length;
          }

          // Fetch pool events from combined transactions endpoint
          yield* logger.info('sync', 'Fetching pool events', { groupId: options.groupId });

          const poolEventsResult = yield* Effect.either(
            pipe(
              api.get(`/groups/${options.groupId}/settlements/transactions`),
              Effect.retry(retrySchedule)
            )
          );

          let poolEvents: any[] = [];
          if (poolEventsResult._tag === 'Right') {
            const poolEventsResponse: any = poolEventsResult.right;

            if (poolEventsResponse?.notModified === true) {
              receivedNotModified = true;
              yield* logger.info('sync', 'Pool events not modified (304)');
            } else {
              const transactions = Array.isArray(poolEventsResponse)
                ? poolEventsResponse
                : poolEventsResponse?.transactions || [];
              poolEvents = transactions.filter((tx: any) => tx.type === 'pool_event');
              yield* logger.info('sync', 'Pool events fetched', { count: poolEvents.length });
            }
          } else {
            yield* logger.warn(
              'sync',
              'Failed to fetch pool events, continuing without them',
              {},
              poolEventsResult.left instanceof Error ? poolEventsResult.left : undefined
            );
          }

          if (poolEvents.length > 0) {
            const existingTransactions = yield* storage.getTransactionsByGroup(options.groupId);
            const existingMap = new Map(existingTransactions.map(t => [t.id, t]));

            const poolEventsToStore: TransactionRecord[] = poolEvents.map((tx: any) => {
              serverTransactionIds.add(tx.id);
              const existing = existingMap.get(tx.id);

              const baseRecord: TransactionRecord = {
                id: tx.id,
                type: 'pool_event',
                group_id: options.groupId,
                created_at: tx.date || tx.created_at || new Date().toISOString(),
                updated_at: tx.updated_at,
                is_synced: true,
                local_changes: false,
                description: tx.description || '',
                amount: tx.amount,
                currency_code: tx.currency || tx.currency_code || 'EUR',
                event_type: tx.event_type,
                pool_id: tx.pool_id,
                pool_name: tx.pool_name,
                expense_id: tx.expense_id ?? null,
                user: tx.user ? {
                  id: tx.user.id,
                  name: tx.user.name,
                  email: tx.user.email,
                  avatar_url: tx.user.avatar_url,
                } : undefined,
              };

              if (existing) {
                return {
                  ...baseRecord,
                  sync_generation: existing.sync_generation,
                  sync_error: existing.local_changes ? existing.sync_error : undefined,
                };
              }

              return baseRecord;
            });

            yield* storage.putTransactions(poolEventsToStore).pipe(
              Effect.mapError(error => new SyncError('Failed to store pool events', error, options.groupId))
            );

            totalRecords += poolEvents.length;
          }

          // Clean up: Remove local synced transactions that no longer exist on server
          // Skip cleanup if we received any 304 Not Modified responses, as we don't have
          // a complete server state to compare against
          if (!receivedNotModified) {
            yield* logger.info('sync', 'Starting cleanup phase', {
              groupId: options.groupId,
              serverTransactionCount: serverTransactionIds.size,
              serverTransactionIds: Array.from(serverTransactionIds).slice(0, 10) // Log first 10 IDs for debugging
            });

            // Get local transactions for this group
            // Note: getTransactionsByGroup already filters out deleted ones, which is what we want
            // We only want to remove synced transactions that are no longer on the server
            const allLocalTransactions = yield* storage.getTransactionsByGroup(options.groupId);

            yield* logger.info('sync', 'Local transactions before cleanup', {
              count: allLocalTransactions.length,
              localIds: allLocalTransactions.map(t => ({
                id: t.id,
                is_synced: t.is_synced,
                deleted: t.deleted,
                local_changes: t.local_changes,
                type: t.type
              }))
            });

            // Now check local transactions and remove any that are:
            // 1. Synced (is_synced: true) - meaning they came from the server originally
            // 2. Not in the server set - meaning they were deleted on the server
            // 3. NOT marked as deleted locally (those need to stay until synced)
            let deletedCount = 0;
            for (const localTx of allLocalTransactions) {
              // Skip any transactions with local IDs (not synced yet)
              if (localTx.id.startsWith('local-')) {
                yield* logger.debug('sync', 'Skipping local transaction in cleanup', {
                  id: localTx.id,
                  is_synced: localTx.is_synced,
                  local_changes: localTx.local_changes
                });
                continue;
              }

              const isInServerSet = serverTransactionIds.has(localTx.id);
              const shouldDelete = localTx.is_synced && !localTx.deleted && !isInServerSet;

              yield* logger.debug('sync', 'Evaluating transaction for cleanup', {
                id: localTx.id,
                is_synced: localTx.is_synced,
                deleted: localTx.deleted,
                local_changes: localTx.local_changes,
                isInServerSet,
                shouldDelete
              });

              if (shouldDelete) {
              yield* logger.warn('sync', 'Removing transaction that appears deleted on server', {
                id: localTx.id,
                type: localTx.type,
                description: localTx.description,
                was_synced: localTx.is_synced,
                had_local_changes: localTx.local_changes
              });
              yield* storage.deleteTransaction(localTx.id);

              // Publish deletion event so UI updates
              yield* events.publish({
                _tag: 'TransactionDeleted',
                transactionId: localTx.id,
                groupId: options.groupId,
                timestamp: yield* clock.isoString(),
              });

              deletedCount++;
            }
          }

            if (deletedCount > 0) {
              yield* logger.warn('sync', 'Cleanup removed transactions from local storage', {
                count: deletedCount,
                groupId: options.groupId
              });
            } else {
              yield* logger.info('sync', 'Cleanup complete, no transactions removed', {
                groupId: options.groupId
              });
            }
          } else {
            yield* logger.info('sync', 'Skipping deleted transaction cleanup (received 304 Not Modified)');
          }

          const endTime = yield* clock.timestamp();
          const duration = endTime - startTime;

          // Update metadata to Complete
          yield* storage.putSyncMetadata({
            groupId: options.groupId,
            state: SyncState.Complete(timestamp, totalRecords, duration),
            lastFullSyncAt: options.mode === 'full' ? timestamp : metadata?.lastFullSyncAt || null,
            lastIncrementalSyncAt: options.mode === 'incremental' ? timestamp : metadata?.lastIncrementalSyncAt || null,
            cursor: null,
            generation: currentGeneration + 1,
          });

          // Emit completion event
          yield* events.publish(
            Events.syncComplete(options.groupId, totalRecords, duration, timestamp)
          );

          yield* logger.info('sync', 'Sync complete', {
            groupId: options.groupId,
            recordsSynced: totalRecords,
            duration,
          });

          return {
            groupId: options.groupId,
            recordsSynced: totalRecords,
            conflicts: 0,
            duration,
          };
        });

        return pipe(
          effect,
          Effect.mapError(error =>
            error instanceof SyncError
              ? error
              : new SyncError('Sync failed', error, options.groupId)
          )
        );
      },

      pushLocalChanges: (groupId) => {
        const effect = Effect.gen(function* () {
          yield* logger.info('sync', 'Pushing local changes', { groupId });

          const unsynced = yield* storage.getUnsyncedTransactions();
          const groupUnsynced = unsynced.filter(t => t.group_id === groupId);

          let pushed = 0;
          let failed = 0;

          for (const transaction of groupUnsynced) {
            const pushTransaction = Effect.gen(function* () {
              // A record created locally that has never reached the server. New
              // expenses use a client UUID + `pending_create`; settlements (and
              // any legacy records) still use the `local-` id prefix.
              const isPendingCreate = transaction.pending_create === true
                || transaction.id.startsWith('local-');

              // Deletions are handled FIRST so that a record created and then
              // deleted while offline is simply dropped locally, never sent to
              // the server (previously it was created and then resurrected).
              if (transaction.deleted) {
                if (isPendingCreate) {
                  yield* logger.info('sync', 'Dropping never-synced deleted transaction', { id: transaction.id });
                  yield* storage.deleteTransaction(transaction.id);
                  return;
                }
                if (transaction.type === 'settlement') {
                  yield* logger.info('sync', 'Deleting settlement on server', { id: transaction.id });
                  yield* api.delete(`/groups/${transaction.group_id}/settlements/${transaction.id}`);
                } else {
                  yield* logger.info('sync', 'Deleting expense on server', { id: transaction.id });
                  yield* api.delete(`/expenses/${transaction.id}`);
                }
                yield* storage.deleteTransaction(transaction.id);
                return;
              }

              if (isPendingCreate) {
                if (transaction.type === 'pool_event') {
                  yield* logger.warn('sync', 'Skipping local pool_event push (server-managed only)', {
                    id: transaction.id
                  });
                  return;
                }

                if (transaction.type === 'settlement') {
                  // Settlements still get a server-assigned id (the create
                  // endpoint does not accept a client id), so keep the
                  // delete-local / store-server-id swap.
                  yield* logger.info('sync', 'Creating settlement on server', { id: transaction.id });
                  const serverResponse: any = yield* api.post(`/groups/${transaction.group_id}/settlements`, {
                    from_user_id: transaction.from_user_id,
                    to_user_id: transaction.to_user_id,
                    amount: transaction.amount,
                    currency_code: transaction.currency_code,
                    description: transaction.description || '',
                  });
                  const serverTransaction = serverResponse?.settlement;
                  if (serverTransaction?.id) {
                    yield* storage.deleteTransaction(transaction.id);
                    const serverRecord: TransactionRecord = {
                      ...transaction,
                      id: serverTransaction.id,
                      pending_create: false,
                      is_synced: true,
                      local_changes: false,
                      created_at: serverTransaction.created_at || transaction.created_at,
                      updated_at: serverTransaction.updated_at || transaction.updated_at,
                    };
                    yield* storage.putTransaction(serverRecord);
                    const timestamp = new Date().toISOString();
                    yield* events.publish({
                      _tag: 'TransactionUpdated',
                      transaction: serverRecord,
                      groupId: transaction.group_id,
                      timestamp,
                    });
                  }
                  return;
                }

                // Expense create. Records authored with a client-generated UUID
                // (pending_create) send their id so the server create is
                // idempotent — a retried push whose response was lost is a
                // no-op, never a duplicate. Legacy `local-`-prefixed records
                // have no valid UUID, so they omit the id and get remapped to
                // the server-assigned id the old way.
                const usesClientId = transaction.pending_create === true;
                yield* logger.info('sync', 'Creating expense on server', { id: transaction.id });
                const transactionDate = transaction.date || transaction.created_at || new Date().toISOString();
                const serverResponse: any = yield* api.post('/expenses/create', {
                  ...(usesClientId ? { id: transaction.id } : {}),
                  group: transaction.group_id,
                  category: transaction.category_id,
                  description: transaction.description,
                  amount: transaction.amount,
                  currency_code: transaction.currency_code,
                  paid_by: transaction.paid_by,
                  date: transactionDate,
                  // Only sent for deliberate custom splits; strip to exactly
                  // {user_id, weight} — the API rejects extra properties.
                  ...(transaction.splits?.length ? {
                    splits: transaction.splits.map(({ user_id, weight }) => ({ user_id, weight })),
                  } : {}),
                });
                const serverTransaction = serverResponse?.expense;

                if (usesClientId) {
                  // The id is already canonical — clear the pending flag and
                  // mark synced in place (no delete/reinsert, no id remap).
                  const syncedRecord: TransactionRecord = {
                    ...transaction,
                    pending_create: false,
                    is_synced: true,
                    local_changes: false,
                    created_at: serverTransaction?.created_at || transaction.created_at || transaction.date,
                    updated_at: serverTransaction?.updated_at || transaction.updated_at,
                    created_by: serverTransaction?.created_by || transaction.created_by,
                  };
                  yield* storage.putTransaction(syncedRecord);
                  yield* logger.info('sync', 'Expense created on server (idempotent)', { id: transaction.id });
                  const timestamp = new Date().toISOString();
                  yield* events.publish({
                    _tag: 'TransactionUpdated',
                    transaction: syncedRecord,
                    groupId: transaction.group_id,
                    timestamp,
                  });
                } else if (serverTransaction?.id) {
                  // Legacy local- id: replace with the server-assigned id.
                  yield* storage.deleteTransaction(transaction.id);
                  const serverRecord: TransactionRecord = {
                    ...transaction,
                    id: serverTransaction.id,
                    pending_create: false,
                    is_synced: true,
                    local_changes: false,
                    date: serverTransaction.created_at || transaction.date || transaction.created_at,
                    created_at: serverTransaction.created_at || transaction.created_at || transaction.date,
                    created_by: serverTransaction.created_by || transaction.created_by,
                    updated_at: serverTransaction.updated_at || transaction.updated_at,
                  };
                  yield* storage.putTransaction(serverRecord);
                  yield* logger.info('sync', 'Expense created on server and stored locally', {
                    localId: transaction.id,
                    serverId: serverTransaction.id
                  });
                  const timestamp = new Date().toISOString();
                  yield* events.publish({
                    _tag: 'TransactionUpdated',
                    transaction: serverRecord,
                    groupId: transaction.group_id,
                    timestamp,
                  });
                }
                return;
              }

              // Update an existing (previously synced) transaction.
              if (transaction.type === 'settlement') {
                // Settlements are immutable by design (no edit UI, no backend
                // PUT route). A dirty settlement record can only be legacy
                // residue; pushing it would 404 forever, so drop the local
                // change and keep the server version authoritative.
                yield* logger.warn('sync', 'Ignoring local edit of immutable settlement', { id: transaction.id });
                yield* storage.putTransaction({
                  ...transaction,
                  is_synced: true,
                  local_changes: false,
                });
                return;
              } else {
                yield* logger.info('sync', 'Updating expense on server', { id: transaction.id });
                yield* api.put(`/expenses/${transaction.id}`, {
                  description: transaction.description,
                  amount: transaction.amount,
                  category_id: transaction.category_id,
                  date: transaction.date,
                  paid_by: transaction.paid_by,
                  ...(transaction.splits?.length ? {
                    splits: transaction.splits.map(({ user_id, weight }) => ({ user_id, weight })),
                  } : {}),
                });
              }

              yield* storage.putTransaction({
                ...transaction,
                is_synced: true,
                local_changes: false,
              });
              yield* logger.info('sync', 'Transaction updated and marked as synced', { id: transaction.id });
            });

            const result = yield* pipe(
              pushTransaction,
              Effect.match({
                onFailure: (error) => {
                  // Log error and return failure marker
                  return Effect.gen(function* () {
                    yield* logger.error('sync', 'Failed to push transaction', { transactionId: transaction.id, type: transaction.type }, error instanceof Error ? error : undefined);
                    return { success: false };
                  });
                },
                onSuccess: () => Effect.succeed({ success: true }),
              }),
              Effect.flatten
            );

            if (result.success) {
              pushed++;
            } else {
              failed++;
            }
          }

          yield* logger.info('sync', 'Push complete', { groupId, pushed, failed });

          return { pushed, failed };
        });

        return pipe(
          effect,
          Effect.mapError(error => new SyncError('Push failed', error, groupId))
        );
      },

      getSyncStatus: (groupId) =>
        pipe(
          storage.getSyncMetadata(groupId),
          Effect.catchAll(() => Effect.succeed(null))
        ),
    };
  })
);
